import type { BootstrapResponse, FeatureRef, Message, SendMessagePayload, StreamEvent, Ticket } from '@dreamteam/chat-protocol';
import { CLOSED_TICKET_STATUSES } from '@dreamteam/chat-protocol';
import { ApiClient } from '../api.js';
import { WIDGET_ATTR } from '../capture/dom.js';
import type { CaptureRecorder } from '../capture/recorder.js';
import type { HostAdapter, HostChangelogEntry, HostFeature } from '../host.js';
import { takeScreenshot, type ScreenshotResult } from '../screenshot.js';
import { VoiceRecorder, voiceSupported, type VoiceClip } from '../voice.js';
import { dict, type Dict } from './i18n.js';
import { icons } from './icons.js';
import { styles } from './styles.js';

export interface WidgetUiOptions {
  locale: 'ru' | 'en';
  accent: string;
  position: 'right' | 'left';
  zIndex: number;
  theme: 'auto' | 'light' | 'dark';
  launcher: boolean;
  consent: 'granted' | 'ask';
  screenshotMethod: 'dom' | 'display';
  title?: string;
  /** Разрешённые расширения файлов-вложений и предел размера (как на сервере). */
  fileExtensions: string[];
  maxFileBytes: number;
  host: HostAdapter;
  onEvent?: (e: StreamEvent) => void;
}

type Tab = 'request' | 'tickets' | 'features';

type Target =
  | { mode: 'auto' }
  | { mode: 'new' }
  | { mode: 'ticket'; ticketId: string; reason: 'reply' | 'append'; replyTo?: string | null };

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const store = {
  get(k: string) {
    try {
      return localStorage.getItem(`dreamteam-chat:${k}`);
    } catch {
      return null;
    }
  },
  set(k: string, v: string) {
    try {
      localStorage.setItem(`dreamteam-chat:${k}`, v);
    } catch {
      /* private mode */
    }
  },
};

function uid() {
  return (crypto as any).randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const fmtSize = (n: number) => (n < 1024 ? `${n} Б` : n < 1024 * 1024 ? `${Math.round(n / 1024)} КБ` : `${(n / 1024 / 1024).toFixed(1)} МБ`);

export class ChatWidget {
  private t: Dict;
  private host!: HTMLElement;
  private shadow!: ShadowRoot;
  private el: Record<string, HTMLElement> = {};
  private boot: BootstrapResponse | null = null;
  private tickets = new Map<string, Ticket>();
  private messages: Message[] = [];
  private hasMore = false;
  private tab: Tab = 'request';
  private opened = false;
  private unread = 0;
  private connected = true;
  private sending = false;
  private error = '';
  private target: Target = { mode: 'auto' };
  private shots: ScreenshotResult[] = [];
  private voice: VoiceClip | null = null;
  private files: File[] = [];
  private recorder = new VoiceRecorder();
  private recTimer: ReturnType<typeof setInterval> | null = null;
  private consent: boolean;
  private stopStream: (() => void) | null = null;
  private eventsTimer: ReturnType<typeof setInterval> | null = null;
  /** Поток событий держится только пока панель открыта и вкладка видна (16.10.2026): иначе каждая вкладка хоста держала
   *  постоянное соединение, а у Chrome без HTTP/2 их всего 6 на сайт — страницы хоста переставали открываться. С закрытой
   *  панелью непрочитанное подтягивается опросом раз в QUIET_MS и при возвращении во вкладку. */
  private quietTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly QUIET_MS = 180_000;
  private onVisibility = () => this.syncStream();
  private features: HostFeature[] = [];
  private changelog: HostChangelogEntry[] = [];
  private themeObserver: MutationObserver | null = null;

  constructor(private api: ApiClient, private capture: CaptureRecorder, private opts: WidgetUiOptions) {
    this.t = dict(opts.locale);
    const saved = store.get('consent');
    this.consent = opts.consent === 'granted' ? saved !== '0' : saved === '1';
  }

  private get hasFeatures() {
    return typeof this.opts.host.features === 'function';
  }

  /* ================================================================ */
  /* Mount                                                             */
  /* ================================================================ */

  mount(parent: HTMLElement = document.body) {
    this.host = document.createElement('div');
    this.host.setAttribute(WIDGET_ATTR, 'root');
    parent.appendChild(this.host);
    this.shadow = this.host.attachShadow({ mode: 'open' });
    const t = this.t;
    const accept = this.opts.fileExtensions.map((e) => `.${e}`).join(',');
    this.shadow.innerHTML = `
      <style>${styles(this.opts.accent, this.opts.position, this.opts.zIndex)}</style>
      <div class="root" part="root">
        <section class="panel" role="dialog" aria-label="${esc(this.opts.title ?? t.title)}" hidden>
          <header class="head">
            <div class="head-row">
              <h2>${esc(this.opts.title ?? t.title)}</h2>
              <button class="icon-btn" data-act="close" title="${t.close}" aria-label="${t.close}">${icons.x}</button>
            </div>
            <p>${esc(t.subtitle)}</p>
            <nav class="tabs" role="tablist">
              <button class="tab" role="tab" data-tab="request" aria-selected="true">${icons.chat}${t.tabRequest}</button>
              <button class="tab" role="tab" data-tab="tickets" aria-selected="false">${icons.list}${t.tabTickets}<span class="count" data-ref="ticketCount"></span></button>
              <button class="tab" role="tab" data-tab="features" aria-selected="false" ${this.hasFeatures ? '' : 'hidden'}>${icons.flag}${t.tabFeatures}<span class="count" data-ref="featureCount"></span></button>
            </nav>
          </header>
          <div class="offline" data-ref="offline" hidden>${t.offline}</div>
          <div class="view" data-view="request">
            <div class="list" data-ref="list" aria-live="polite"></div>
            <div class="composer">
              <div class="target" data-ref="target"></div>
              <div class="tray" data-ref="tray"></div>
              <div class="row" data-ref="row">
                <textarea data-ref="input" rows="1" placeholder="${esc(t.placeholder)}" aria-label="${esc(t.placeholder)}"></textarea>
                <div class="recbar" data-ref="recbar" hidden><span class="dot"></span><span data-ref="recTime">0:00</span><span class="level"><i data-ref="level"></i></span></div>
                <button class="tool" data-act="rec" data-ref="recBtn" title="${esc(t.recordHint)}" aria-label="${t.record}">${icons.record}</button>
                <button class="tool" data-act="shot" title="${t.screenshot}" aria-label="${t.screenshot}">${icons.crop}</button>
                <button class="tool" data-act="mic" title="${t.voice}" aria-label="${t.voice}" ${voiceSupported() ? '' : 'hidden'}>${icons.mic}</button>
                <button class="tool" data-act="file" title="${t.file}" aria-label="${t.file}">${icons.clip}</button>
                <input type="file" data-ref="fileInput" multiple accept="${esc(accept)}" hidden />
                <button class="tool send" data-act="send" title="${t.send}" aria-label="${t.send}" disabled>${icons.send}</button>
              </div>
              <div class="err" data-ref="err" hidden></div>
              <div class="foot" data-ref="foot"></div>
            </div>
          </div>
          <div class="view" data-view="tickets" hidden>
            <div class="forks" data-ref="ticketList"></div>
          </div>
          <div class="view" data-view="features" hidden>
            <div class="forks" data-ref="featureList"></div>
          </div>
        </section>
        <button class="launcher" data-act="toggle" aria-label="${t.open}" title="${t.open}" ${this.opts.launcher ? '' : 'hidden'}>${icons.chat}<span class="badge" data-ref="badge" hidden></span><span class="recdot" data-ref="recdot" hidden></span></button>
      </div>`;
    const q = (s: string) => this.shadow.querySelector(s) as HTMLElement;
    this.el.root = q('.root');
    this.el.panel = q('.panel');
    this.shadow.querySelectorAll<HTMLElement>('[data-ref]').forEach((n) => (this.el[n.dataset.ref!] = n));
    this.applyTheme();
    this.bind();
    if (store.get('open') === '1') this.open();
  }

  private applyTheme() {
    let theme = this.opts.theme === 'auto' ? '' : this.opts.theme;
    if (this.opts.host.theme) theme = this.opts.host.theme();
    this.el.root.classList.toggle('dark', theme === 'dark');
    this.el.root.classList.toggle('light', theme === 'light');
    if (this.opts.host.theme && !this.themeObserver && typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => this.applyTheme());
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    }
  }

  private bind() {
    this.shadow.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-act],[data-tab]');
      if (!btn) return;
      if (btn.dataset.tab) return this.setTab(btn.dataset.tab as Tab);
      const act = btn.dataset.act!;
      const id = btn.dataset.id ?? '';
      switch (act) {
        case 'toggle':
          return this.toggle();
        case 'close':
          return this.close();
        case 'send':
          return void this.send();
        case 'shot':
          return void this.screenshot();
        case 'mic':
          return void this.toggleVoice();
        case 'rec':
          return this.toggleRecording();
        case 'file':
          return (this.el.fileInput as HTMLInputElement).click();
        case 'submit':
          return void this.submitCurrent();
        case 'new-ticket':
          this.target = { mode: 'new' };
          return this.renderComposer();
        case 'auto-ticket':
          this.target = { mode: 'auto' };
          return this.renderComposer();
        case 'rm-shot':
          URL.revokeObjectURL(this.shots[Number(id)]?.previewUrl ?? '');
          this.shots.splice(Number(id), 1);
          return this.renderComposer();
        case 'rm-voice':
          if (this.voice) URL.revokeObjectURL(this.voice.url);
          this.voice = null;
          return this.renderComposer();
        case 'rm-file':
          this.files.splice(Number(id), 1);
          return this.renderComposer();
        case 'reply': {
          const m = this.messages.find((x) => x.id === id);
          if (m) this.target = { mode: 'ticket', ticketId: m.ticketId, reason: 'reply', replyTo: m.id };
          this.renderComposer();
          return (this.el.input as HTMLTextAreaElement).focus();
        }
        case 'option': {
          const m = this.messages.find((x) => x.id === id);
          if (!m) return;
          this.target = { mode: 'ticket', ticketId: m.ticketId, reason: 'reply', replyTo: m.id };
          (this.el.input as HTMLTextAreaElement).value = btn.dataset.value ?? '';
          return void this.send();
        }
        case 'append': {
          this.target = { mode: 'ticket', ticketId: id, reason: 'append' };
          this.setTab('request');
          this.renderComposer();
          this.scrollToTicket(id);
          return (this.el.input as HTMLTextAreaElement).focus();
        }
        case 'verdict':
          return void this.verdict(id, btn.dataset.ok === '1');
        case 'show': {
          const route = btn.dataset.route;
          if (route && this.opts.host.navigate) {
            this.close();
            this.opts.host.navigate(route, btn.dataset.anchor || null);
          }
          return;
        }
        case 'feature-on':
          return void this.featureToggle(id, true);
        case 'feature-off':
          return void this.featureToggle(id, false);
        case 'feature-share':
          return void this.featureShare(id, btn.dataset.vis as 'me' | 'group');
        case 'more':
          return void this.loadMore();
        case 'open-shot': {
          const lb = document.createElement('div');
          lb.className = 'lightbox';
          lb.innerHTML = `<img alt="" src="${esc(btn.dataset.src ?? '')}"/>`;
          lb.addEventListener('click', () => lb.remove());
          this.el.root.appendChild(lb);
          return;
        }
      }
    });
    const input = this.el.input as HTMLTextAreaElement;
    input.addEventListener('input', () => {
      input.style.height = 'auto';
      input.style.height = `${Math.min(140, input.scrollHeight)}px`;
      this.updateSendState();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        void this.send();
      }
    });
    (this.el.fileInput as HTMLInputElement).addEventListener('change', (e) => {
      const inp = e.target as HTMLInputElement;
      this.addFiles([...(inp.files ?? [])]);
      inp.value = '';
    });
    this.shadow.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      if (el.dataset?.ref === 'consent') {
        this.consent = el.checked;
        store.set('consent', this.consent ? '1' : '0');
        this.renderFoot();
      }
    });
    this.shadow.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape' && this.opened && !this.recorder.recording) this.close();
    });
  }

  /* ================================================================ */
  /* Lifecycle                                                         */
  /* ================================================================ */

  async start() {
    try {
      this.boot = await this.api.bootstrap();
      for (const tk of this.boot.tickets) this.tickets.set(tk.id, tk);
      if (this.boot.currentTicket) this.tickets.set(this.boot.currentTicket.id, this.boot.currentTicket);
      const h = await this.api.history(undefined, 50);
      this.ingestHistory(h.tickets, h.messages, h.hasMore);
      this.connected = true;
    } catch (e) {
      this.connected = false;
      this.error = String((e as Error).message ?? e);
    }
    this.renderAll();
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onVisibility);
    this.syncStream();
    this.eventsTimer = setInterval(() => this.renderFoot(), 2000);
  }

  /** Открыть поток, если панель открыта и вкладка видна; иначе закрыть поток и перейти на редкий опрос. */
  private syncStream() {
    const want = this.opened && document.visibilityState === 'visible';
    if (want && !this.stopStream) {
      this.stopStream = this.api.stream(
        (e) => this.onStream(e),
        (c) => {
          this.connected = c;
          this.el.offline.hidden = c;
        },
      );
      if (this.quietTimer) { clearInterval(this.quietTimer); this.quietTimer = null; }
      void this.refreshQuiet(); // догнать то, что пришло, пока потока не было
    } else if (!want && this.stopStream) {
      this.stopStream();
      this.stopStream = null;
      this.el.offline.hidden = true;
    }
    if (!want && !this.quietTimer && document.visibilityState === 'visible') {
      this.quietTimer = setInterval(() => void this.refreshQuiet(), ChatWidget.QUIET_MS);
    }
    if (document.visibilityState !== 'visible' && this.quietTimer) { clearInterval(this.quietTimer); this.quietTimer = null; }
    if (!want && document.visibilityState === 'visible') void this.refreshQuiet();
  }

  private refreshing = false;
  /** Подтянуть свежие сообщения и заявки без потока: один запрос истории; новые чужие сообщения при закрытой панели — в бейдж. */
  private async refreshQuiet() {
    if (this.refreshing || !this.boot) return;
    this.refreshing = true;
    try {
      const h = await this.api.history(undefined, 20);
      const known = new Set(this.messages.map((m) => m.id));
      const fresh = h.messages.filter((m) => !known.has(m.id));
      this.ingestHistory(h.tickets, h.messages, this.hasMore);
      const foreign = fresh.filter((m) => m.author !== 'user').length;
      if (foreign && !this.opened) { this.unread += foreign; this.renderBadge(); }
      if (fresh.length || h.tickets.length) { this.renderList(fresh.length > 0); this.renderTickets(); this.renderComposer(); }
      this.connected = true;
    } catch {
      /* без сети — покажет следующий опрос или поток */
    } finally {
      this.refreshing = false;
    }
  }

  destroy() {
    this.stopStream?.();
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onVisibility);
    if (this.quietTimer) clearInterval(this.quietTimer);
    if (this.eventsTimer) clearInterval(this.eventsTimer);
    if (this.recTimer) clearInterval(this.recTimer);
    this.recorder.cancel();
    this.themeObserver?.disconnect();
    this.host?.remove();
  }

  open() {
    this.opened = true;
    this.el.panel.hidden = false;
    this.unread = 0;
    this.renderBadge();
    store.set('open', '1');
    this.scrollToBottom();
    setTimeout(() => (this.el.input as HTMLTextAreaElement).focus(), 50);
    if (this.boot) this.syncStream(); // до start() потока ещё нет — его откроет start()
  }

  close() {
    this.opened = false;
    this.el.panel.hidden = true;
    store.set('open', '0');
    this.syncStream();
  }

  toggle() {
    if (this.opened) this.close();
    else this.open();
  }

  setTab(tab: Tab) {
    if (tab === 'features' && !this.hasFeatures) tab = 'request';
    this.tab = tab;
    this.shadow.querySelectorAll<HTMLElement>('.tab').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.tab === tab)));
    this.shadow.querySelectorAll<HTMLElement>('.view').forEach((v) => (v.hidden = v.dataset.view !== tab));
    if (tab === 'tickets') {
      this.renderTickets();
      void this.api.tickets().then((r) => {
        for (const tk of r.tickets) this.tickets.set(tk.id, tk);
        this.renderTickets();
      }).catch(() => {});
    }
    if (tab === 'features') void this.loadFeatures();
    if (tab === 'request') this.scrollToBottom();
  }

  /** Поставить composer в режим ответа/дописывания для заявки (API для хоста). */
  focusTicket(ticketId: string) {
    this.target = { mode: 'ticket', ticketId, reason: 'append' };
    this.open();
    this.setTab('request');
    this.renderComposer();
  }

  /* ================================================================ */
  /* Data                                                              */
  /* ================================================================ */

  private ingestHistory(tickets: Ticket[], messages: Message[], hasMore: boolean) {
    for (const t of tickets) this.tickets.set(t.id, t);
    const known = new Set(this.messages.map((m) => m.id));
    this.messages.push(...messages.filter((m) => !known.has(m.id)));
    this.messages.sort((a, b) => (a.id < b.id ? -1 : 1));
    this.hasMore = hasMore;
  }

  private async loadMore() {
    const first = this.messages[0];
    if (!first) return;
    try {
      const h = await this.api.history(first.id, 50);
      const prevHeight = this.el.list.scrollHeight;
      this.ingestHistory(h.tickets, h.messages, h.hasMore);
      this.renderList(false);
      this.el.list.scrollTop = this.el.list.scrollHeight - prevHeight;
    } catch (e: any) {
      this.error = String(e?.message ?? e);
      this.renderComposer();
    }
  }

  private upsertMessage(m: Message) {
    const i = this.messages.findIndex((x) => x.id === m.id);
    if (i >= 0) this.messages[i] = m;
    else {
      this.messages.push(m);
      this.messages.sort((a, b) => (a.id < b.id ? -1 : 1));
    }
  }

  private onStream(e: StreamEvent) {
    this.opts.onEvent?.(e);
    switch (e.type) {
      case 'message.created':
      case 'message.updated': {
        const isNew = !this.messages.some((m) => m.id === e.message.id);
        this.upsertMessage(e.message);
        if (isNew && e.message.author !== 'user' && !this.opened) {
          this.unread++;
          this.renderBadge();
        }
        this.renderList(isNew);
        if (isNew && e.message.kind === 'feature' && this.hasFeatures) void this.loadFeatures();
        break;
      }
      case 'ticket.updated':
        this.tickets.set(e.ticket.id, e.ticket);
        if (this.boot && (this.boot.currentTicket?.id === e.ticket.id || !this.boot.currentTicket)) {
          this.boot.currentTicket = CLOSED_TICKET_STATUSES.includes(e.ticket.status) ? null : e.ticket;
        }
        this.renderList(false);
        this.renderComposer();
        this.renderTickets();
        break;
    }
  }

  /** Заявка, в которую уйдёт следующее сообщение (подсказка; решает сервер). */
  private currentTicket(): Ticket | null {
    const t = this.boot?.currentTicket ? this.tickets.get(this.boot.currentTicket.id) ?? this.boot.currentTicket : null;
    if (!t || CLOSED_TICKET_STATUSES.includes(t.status)) return null;
    if (t.status === 'question') return t;
    if (this.boot?.app.groupingMode === 'single') return null;
    const last = new Date(t.lastUserMessageAt ?? t.createdAt);
    const windowMs = (this.boot?.app.groupingWindowMinutes ?? 30) * 60_000;
    if (Date.now() - last.getTime() > windowMs) return null;
    if (last.toDateString() !== new Date().toDateString()) return null;
    return t;
  }

  /* ================================================================ */
  /* Recording of actions                                              */
  /* ================================================================ */

  toggleRecording() {
    if (this.capture.recording) this.stopRecording();
    else this.startRecording();
  }

  startRecording() {
    this.capture.start();
    this.renderFoot();
    this.renderRecButton();
  }

  stopRecording() {
    this.capture.stop();
    this.renderFoot();
    this.renderRecButton();
  }

  private renderRecButton() {
    const b = this.el.recBtn;
    const on = this.capture.recording;
    b.classList.toggle('rec', on);
    b.innerHTML = on ? icons.stop : icons.record;
    b.title = on ? this.t.recStop : this.t.recordHint;
    this.el.recdot.hidden = !on;
  }

  /* ================================================================ */
  /* Actions                                                           */
  /* ================================================================ */

  private addFiles(list: File[]) {
    const allowed = new Set(this.opts.fileExtensions.map((e) => e.toLowerCase()));
    for (const f of list) {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      if (!allowed.has(ext)) {
        this.error = this.t.fileType(f.name);
        continue;
      }
      if (f.size > this.opts.maxFileBytes) {
        this.error = this.t.fileTooBig(f.name);
        continue;
      }
      if (!this.files.some((x) => x.name === f.name && x.size === f.size)) this.files.push(f);
    }
    this.renderComposer();
  }

  private updateSendState() {
    const has = !!(this.el.input as HTMLTextAreaElement).value.trim() || this.shots.length > 0 || !!this.voice || this.files.length > 0;
    (this.shadow.querySelector('[data-act="send"]') as HTMLButtonElement).disabled = !has || this.sending || this.recorder.recording;
  }

  async send() {
    const input = this.el.input as HTMLTextAreaElement;
    const text = input.value.trim();
    if (this.sending || this.recorder.recording || (!text && !this.shots.length && !this.voice && !this.files.length)) return;
    this.sending = true;
    this.error = '';
    this.updateSendState();
    // Запись действий заканчивается вместе с отправкой заявки.
    if (this.capture.recording) this.stopRecording();
    const manual = this.capture.mode === 'manual';
    const withEvents = manual ? !!this.capture.session : this.consent;
    const drained = withEvents ? this.capture.drain() : { events: [], dropped: 0 };
    const recording = this.capture.takeSession();
    const payload: SendMessagePayload = {
      clientId: uid(),
      text,
      newTicket: this.target.mode === 'new' || undefined,
      ticketId: this.target.mode === 'ticket' ? this.target.ticketId : undefined,
      replyTo: this.target.mode === 'ticket' ? this.target.replyTo ?? null : null,
      context: {
        consent: withEvents,
        env: this.capture.environment(this.opts.host.appState?.()),
        events: drained.events,
        dropped: drained.dropped,
        recording,
      },
      screenshots: this.shots.map((s, i) => ({ field: `shot${i}`, selection: s.selection, width: s.width, height: s.height })),
      audio: this.voice ? { field: 'audio', durationMs: this.voice.durationMs } : null,
      files: this.files.map((f, i) => ({ field: `file${i}`, name: f.name, size: f.size, mime: f.type || 'application/octet-stream' })),
    };
    const files = [
      ...this.shots.map((s, i) => ({ field: `shot${i}`, blob: s.blob, filename: `shot${i}.png` })),
      ...(this.voice ? [{ field: 'audio', blob: this.voice.blob, filename: `voice.${this.voice.mime.includes('ogg') ? 'ogg' : this.voice.mime.includes('mp4') ? 'm4a' : 'webm'}` }] : []),
      ...this.files.map((f, i) => ({ field: `file${i}`, blob: f as Blob, filename: f.name })),
    ];
    try {
      let res;
      for (let attempt = 0; ; attempt++) {
        try {
          res = await this.api.sendMessage(payload, files);
          break;
        } catch (e: any) {
          if (attempt >= 2 || (e.status && e.status < 500)) throw e;
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
      }
      this.tickets.set(res.ticket.id, res.ticket);
      if (this.boot) this.boot.currentTicket = res.ticket;
      this.upsertMessage(res.message);
      input.value = '';
      input.style.height = '';
      this.shots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      this.shots = [];
      if (this.voice) URL.revokeObjectURL(this.voice.url);
      this.voice = null;
      this.files = [];
      this.target = { mode: 'auto' };
      this.renderList(true);
      this.renderTickets();
    } catch (e: any) {
      this.error = `${this.t.error} (${e?.message ?? e})`;
    } finally {
      this.sending = false;
      this.renderComposer();
    }
  }

  async screenshot() {
    try {
      const res = await takeScreenshot({
        method: this.opts.screenshotMethod,
        hint: this.t.screenshotHint,
        accent: this.opts.accent,
        hideWidget: (hidden) => this.el.root.classList.toggle('hidden-for-capture', hidden),
      });
      if (res) this.shots.push(res);
    } catch (e: any) {
      this.error = String(e?.message ?? e);
    }
    this.renderComposer();
    (this.el.input as HTMLTextAreaElement).focus();
  }

  async toggleVoice() {
    if (this.recorder.recording) {
      const clip = await this.recorder.stop();
      this.stopRecUi();
      if (clip) this.voice = clip;
      this.renderComposer();
      return;
    }
    try {
      if (this.voice) URL.revokeObjectURL(this.voice.url);
      this.voice = null;
      this.recorder.onLevel = (l) => ((this.el.level as HTMLElement).style.width = `${Math.min(100, l * 180)}%`);
      await this.recorder.start((clip) => {
        this.stopRecUi();
        this.voice = clip;
        this.renderComposer();
      });
      this.el.recbar.hidden = false;
      this.el.input.hidden = true;
      const mic = this.shadow.querySelector('[data-act="mic"]') as HTMLElement;
      mic.classList.add('rec');
      mic.innerHTML = icons.stop;
      mic.title = this.t.stop;
      this.recTimer = setInterval(() => {
        const s = Math.floor(this.recorder.elapsedMs / 1000);
        this.el.recTime.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      }, 250);
      this.updateSendState();
    } catch (e: any) {
      this.error = String(e?.message ?? e);
      this.renderComposer();
    }
  }

  private stopRecUi() {
    if (this.recTimer) clearInterval(this.recTimer);
    this.el.recbar.hidden = true;
    this.el.input.hidden = false;
    const mic = this.shadow.querySelector('[data-act="mic"]') as HTMLElement;
    mic.classList.remove('rec');
    mic.innerHTML = icons.mic;
    mic.title = this.t.voice;
  }

  async submitCurrent() {
    const t = this.currentTicket();
    if (!t) return;
    try {
      const r = await this.api.submitTicket(t.id);
      this.tickets.set(r.ticket.id, r.ticket);
      if (this.boot) this.boot.currentTicket = r.ticket;
    } catch (e: any) {
      this.error = String(e?.message ?? e);
    }
    this.renderAll();
  }

  async verdict(ticketId: string, ok: boolean) {
    try {
      const r = await this.api.verdict(ticketId, ok);
      this.tickets.set(r.ticket.id, r.ticket);
      if (!ok) {
        this.target = { mode: 'ticket', ticketId, reason: 'append' };
        (this.el.input as HTMLTextAreaElement).focus();
      }
    } catch (e: any) {
      this.error = String(e?.message ?? e);
    }
    this.renderAll();
  }

  /* ================================================================ */
  /* Features (host)                                                   */
  /* ================================================================ */

  private async loadFeatures() {
    if (!this.hasFeatures) return;
    try {
      this.features = await this.opts.host.features!();
      this.changelog = this.opts.host.changelog ? await this.opts.host.changelog() : [];
      this.error = '';
    } catch (e: any) {
      this.error = String(e?.message ?? e);
    }
    this.renderFeatures();
    this.renderBadge();
  }

  private async featureToggle(key: string, on: boolean) {
    try {
      await this.opts.host.toggleFeature?.(key, on);
    } catch (e: any) {
      this.error = String(e?.message ?? e);
    }
    await this.loadFeatures();
  }

  private async featureShare(key: string, vis: 'me' | 'group') {
    try {
      await this.opts.host.shareFeature?.(key, vis);
    } catch (e: any) {
      this.error = String(e?.message ?? e);
    }
    await this.loadFeatures();
  }

  /* ================================================================ */
  /* Render                                                            */
  /* ================================================================ */

  private renderAll() {
    this.renderList(true);
    this.renderComposer();
    this.renderTickets();
    this.renderFeatures();
    this.renderBadge();
    this.renderRecButton();
    this.el.offline.hidden = this.connected;
  }

  private renderBadge() {
    const b = this.el.badge;
    b.hidden = !this.unread;
    b.textContent = String(this.unread);
    const open = [...this.tickets.values()].filter((t) => !CLOSED_TICKET_STATUSES.includes(t.status)).length;
    this.el.ticketCount.textContent = open ? String(open) : '';
    const fOn = this.features.filter((f) => f.enabled).length;
    this.el.featureCount.textContent = fOn ? String(fOn) : '';
  }

  private scrollToBottom() {
    const l = this.el.list;
    const go = () => (l.scrollTop = l.scrollHeight);
    go();
    requestAnimationFrame(go);
    l.querySelectorAll('img').forEach((img) => {
      if (!(img as HTMLImageElement).complete) img.addEventListener('load', go, { once: true });
    });
  }

  private scrollToTicket(id: string) {
    const el = this.el.list.querySelector<HTMLElement>(`[data-ticket="${CSS.escape(id)}"]`);
    el?.scrollIntoView({ block: 'start' });
  }

  private fmtTime(iso: string) {
    const d = new Date(iso);
    const today = new Date().toDateString() === d.toDateString();
    const loc = this.opts.locale === 'en' ? 'en-GB' : 'ru-RU';
    return today ? d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString(loc, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  private statusPill(t: Ticket) {
    const cls = t.status === 'feature_ready' || t.status === 'done' ? 'ok' : t.status === 'question' || t.status === 'escalated' ? 'warn' : t.status === 'collecting' ? 'accent' : '';
    return `<span class="pill ${cls}">${esc(this.t.status(t.status))}</span>`;
  }

  renderList(scroll: boolean) {
    const t = this.t;
    const list = this.el.list;
    if (!this.messages.length) {
      list.innerHTML = `<div class="empty">${esc(t.empty)}</div>`;
      return;
    }
    const parts: string[] = this.hasMore ? [`<button class="btn more" data-act="more">${esc(t.loadMore)}</button>`] : [];
    let lastTicket = '';
    for (const m of this.messages) {
      if (m.ticketId !== lastTicket) {
        lastTicket = m.ticketId;
        const tk = this.tickets.get(m.ticketId);
        parts.push(
          `<div class="divider" data-ticket="${esc(m.ticketId)}"><span>${t.ticket} #${tk?.number ?? '?'}</span>${tk?.title ? `<b title="${esc(tk.title)}">${esc(tk.title)}</b>` : ''}${tk ? this.statusPill(tk) : ''}</div>`,
        );
      }
      parts.push(this.renderMessage(m));
    }
    list.innerHTML = parts.join('');
    if (scroll) this.scrollToBottom();
  }

  private renderMessage(m: Message): string {
    const t = this.t;
    const time = this.fmtTime(m.createdAt);
    if (m.author === 'system' && m.kind === 'status') {
      return `<div class="sys"><span>${esc(m.text)} · ${time}</span></div>`;
    }
    if (m.kind === 'feature' && m.meta.feature) {
      const f = m.meta.feature as FeatureRef;
      const tk = this.tickets.get(m.ticketId);
      const waiting = tk?.status === 'feature_ready';
      return `<div class="fork-card">
        <h4>${icons.sparkle} ${t.featureReady}: ${esc(f.title)}</h4>
        <p>${esc(f.description || t.featureHint)}</p>
        <div class="actions">
          ${f.route && this.opts.host.navigate ? `<button class="btn primary" data-act="show" data-route="${esc(f.route)}" data-anchor="${esc(f.anchor ?? '')}">${icons.eye} ${t.show}</button>` : ''}
          ${waiting ? `<button class="btn" data-act="verdict" data-ok="1" data-id="${esc(m.ticketId)}">${icons.check} ${t.fits}</button><button class="btn" data-act="verdict" data-ok="0" data-id="${esc(m.ticketId)}">${t.fix}</button>` : ''}
        </div>
        <div class="meta">${time}</div>
      </div>`;
    }
    const cls = m.author === 'user' ? 'user' : m.kind === 'question' ? 'agent question' : 'agent';
    const who = m.author === 'user' ? '' : `<div class="who">${m.kind === 'question' ? `${icons.info} ${t.question}` : t.agent}</div>`;
    const body: string[] = [];
    if (m.text) body.push(`<div>${esc(m.text)}</div>`);
    for (const a of m.attachments.filter((x) => x.kind === 'audio')) {
      body.push(`<div class="voice"><audio controls preload="none" src="${esc(a.url)}"></audio></div>`);
      if (m.transcriptStatus === 'pending') body.push(`<div class="transcript">${t.transcribing}</div>`);
      else if (m.transcript) body.push(`<div class="transcript">${esc(m.transcript)}</div>`);
      else if (m.transcriptStatus === 'failed') body.push(`<div class="transcript">${t.transcriptFailed}</div>`);
    }
    const docs = m.attachments.filter((x) => x.kind === 'file');
    if (docs.length) body.push(`<div class="files">${docs.map((a) => `<a class="filechip" href="${esc(a.url)}" target="_blank" rel="noopener">${icons.file}<span>${esc(a.name ?? 'файл')}</span><em>${fmtSize(a.size)}</em></a>`).join('')}</div>`);
    if (m.kind === 'question') {
      const opts = (m.meta.options ?? []) as string[];
      body.push(
        `<div class="options">${opts.map((o) => `<button class="chip" data-act="option" data-id="${esc(m.id)}" data-value="${esc(o)}">${esc(o)}</button>`).join('')}<button class="chip" data-act="reply" data-id="${esc(m.id)}">${icons.reply} ${t.answer}</button></div>`,
      );
    }
    const shots = m.attachments.filter((x) => x.kind === 'screenshot');
    const shotsHtml = shots.length
      ? `<div class="shots">${shots
          .map(
            (a) =>
              `<button class="shot" data-act="open-shot" data-src="${esc(a.url)}"><img alt="" loading="lazy" src="${esc(a.url)}"/>${a.selection ? `<span class="cap">${t.elements(a.selection.elements.length)}</span>` : ''}</button>`,
          )
          .join('')}</div>`
      : '';
    const meta = [
      time,
      m.contextEventCount ? t.events(m.contextEventCount) : '',
      m.githubCommentId ? `<span class="linked" title="${t.linked}">${icons.check}</span>` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    return `<div class="msg ${cls}" data-id="${esc(m.id)}">${who}${body.length ? `<div class="bubble">${body.join('')}</div>` : ''}${shotsHtml}<div class="meta">${meta}</div></div>`;
  }

  renderComposer() {
    const t = this.t;
    let target = '';
    if (this.target.mode === 'ticket') {
      const tk = this.tickets.get(this.target.ticketId);
      const label = this.target.reason === 'append' ? t.append : t.replyTo;
      target = `<span>${label} #${tk?.number ?? '?'}</span>${tk?.title ? `<b>${esc(tk.title)}</b>` : ''}<span class="spacer"></span><button class="link" data-act="auto-ticket">${icons.x}</button>`;
    } else if (this.target.mode === 'new') {
      target = `<span>${t.willCreate}</span><span class="spacer"></span><button class="link" data-act="auto-ticket">${icons.x}</button>`;
    } else {
      const cur = this.currentTicket();
      target = cur
        ? `<span>${t.continueAuto} #${cur.number}</span>${cur.title ? `<b>${esc(cur.title)}</b>` : ''}<span class="spacer"></span><button class="link" data-act="new-ticket">${t.startNew}</button>`
        : `<span>${t.newTicket}</span>`;
    }
    this.el.target.innerHTML = target;
    const tray: string[] = this.shots.map(
      (s, i) =>
        `<div class="att"><img alt="" src="${s.previewUrl}"/><span class="lbl">${t.elements(s.selection.elements.length)}</span><button class="rm" data-act="rm-shot" data-id="${i}" aria-label="${t.remove}">${icons.x}</button></div>`,
    );
    if (this.voice) {
      tray.push(`<div class="att"><audio controls src="${this.voice.url}"></audio><span class="lbl">${Math.round(this.voice.durationMs / 1000)} c</span><button class="rm" data-act="rm-voice" aria-label="${t.remove}">${icons.x}</button></div>`);
    }
    for (const [i, f] of this.files.entries()) {
      tray.push(`<div class="att file">${icons.file}<span class="lbl"><b>${esc(f.name)}</b> ${fmtSize(f.size)}</span><button class="rm" data-act="rm-file" data-id="${i}" aria-label="${t.remove}">${icons.x}</button></div>`);
    }
    this.el.tray.innerHTML = tray.join('');
    this.el.err.hidden = !this.error;
    this.el.err.textContent = this.error;
    this.renderFoot();
    this.updateSendState();
  }

  private renderFoot() {
    const t = this.t;
    const cur = this.currentTicket();
    const submit = this.target.mode === 'auto' && cur && cur.status === 'collecting' ? `<span class="spacer"></span><button class="link" data-act="submit">${t.submitNow}</button>` : '';
    if (this.capture.mode === 'manual') {
      const n = this.capture.peek().length;
      const rec = this.capture.recording;
      const text = rec ? `<span class="dot"></span>${t.recording} · ${t.recordedEvents(n)}` : this.capture.session ? t.recordedEvents(n) : t.recordHint;
      this.el.foot.innerHTML = `<span class="${rec ? 'live' : ''}">${text}</span>${submit}`;
    } else {
      const n = this.consent ? this.capture.peek().length : 0;
      this.el.foot.innerHTML = `<label title="${esc(t.consentHint)}"><input type="checkbox" data-ref="consent" ${this.consent ? 'checked' : ''}/> ${t.consent}</label><span>${this.consent && n ? `· ${t.events(n)}` : ''}</span>${submit}`;
    }
  }

  renderTickets() {
    const t = this.t;
    const list = [...this.tickets.values()].sort((a, b) => b.number - a.number);
    if (!list.length) {
      this.el.ticketList.innerHTML = `<div class="empty">${esc(t.noTickets)}</div>`;
      return;
    }
    this.el.ticketList.innerHTML = list
      .map((tk) => {
        const closed = CLOSED_TICKET_STATUSES.includes(tk.status);
        const feature = tk.featureKey ? this.features.find((f) => f.key === tk.featureKey) : undefined;
        const route = feature?.route ?? null;
        const show = route && this.opts.host.navigate ? `<button class="btn" data-act="show" data-route="${esc(route)}" data-anchor="${esc(feature?.anchor ?? '')}">${icons.eye} ${t.show}</button>` : '';
        return `<div class="fork ${tk.status === 'feature_ready' || tk.status === 'question' ? 'active' : ''}">
          <div class="name">#${tk.number} ${tk.title ? esc(tk.title) : ''} ${this.statusPill(tk)}</div>
          <div>${closed ? '' : `<button class="btn" data-act="append" data-id="${esc(tk.id)}">${t.append}</button>`}</div>
          <div class="sub">${this.fmtTime(tk.createdAt)}${tk.githubIssueUrl ? ` · <a class="link" href="${esc(tk.githubIssueUrl)}" target="_blank" rel="noopener">GitHub #${tk.githubIssueNumber}</a>` : ''}${show ? ` · ${show}` : ''}</div>
        </div>`;
      })
      .join('');
  }

  renderFeatures() {
    if (!this.hasFeatures) return;
    const t = this.t;
    const nav = !!this.opts.host.navigate;
    const card = (f: HostFeature) => {
      const meta = [`${t.author}: ${f.mine ? t.you : f.author ?? '—'}`, f.visibility ? t.visibility[f.visibility] ?? f.visibility : ''];
      if (f.daysLeft != null) meta.push(t.daysLeft(f.daysLeft));
      if (f.users && f.users > 1) meta.push(t.users(f.users));
      const acts: string[] = [];
      if (f.enabled && f.route && nav) acts.push(`<button class="btn primary" data-act="show" data-route="${esc(f.route)}" data-anchor="${esc(f.anchor ?? '')}">${icons.eye} ${t.show}</button>`);
      if (this.opts.host.toggleFeature && !f.disabled) acts.push(f.enabled ? `<button class="btn" data-act="feature-off" data-id="${esc(f.key)}">${t.off}</button>` : `<button class="btn primary" data-act="feature-on" data-id="${esc(f.key)}">${t.on}</button>`);
      if (f.mine && this.opts.host.shareFeature && f.visibility !== 'all') acts.push(`<button class="btn" data-act="feature-share" data-id="${esc(f.key)}" data-vis="${f.visibility === 'me' ? 'group' : 'me'}">${f.visibility === 'me' ? t.share : t.unshare}</button>`);
      return `<div class="fork ${f.enabled ? 'active' : ''}">
        <div class="name">${esc(f.title)}</div><div></div>
        ${f.description ? `<div class="desc">${esc(f.description)}</div>` : ''}
        <div class="sub">${esc(meta.filter(Boolean).join(' · '))}</div>
        <div class="actions" style="grid-column:1/-1">${acts.join('')}</div>
      </div>`;
    };
    const on = this.features.filter((f) => f.enabled);
    const off = this.features.filter((f) => !f.enabled);
    const log = this.changelog
      .map(
        (e) => `<div class="fork"><div class="name">${esc(e.version)} <span class="pill">${e.release ? t.release : t.build}</span>${e.date ? `<span class="sub">${esc(e.date)}</span>` : ''}</div><div></div>
          <ul class="log" style="grid-column:1/-1">${e.items
            .map((it) =>
              it.route && nav && it.enabled !== false
                ? `<li><button class="link" data-act="show" data-route="${esc(it.route)}" data-anchor="${esc(it.anchor ?? '')}">${esc(it.text)} →</button></li>`
                : `<li>${esc(it.text)}${it.enabled === false ? ` <span class="pill">${t.disabledItem}</span>` : ''}</li>`,
            )
            .join('')}</ul></div>`,
      )
      .join('');
    this.el.featureList.innerHTML = `
      <h3>${t.myFeatures}</h3>${on.length ? on.map(card).join('') : `<div class="empty">${esc(t.noFeatures)}</div>`}
      ${off.length ? `<h3>${t.available}</h3>${off.map(card).join('')}` : ''}
      ${log ? `<h3>${t.changelog}</h3>${log}` : ''}
      ${this.error && this.tab === 'features' ? `<div class="err">${esc(this.error)}</div>` : ''}`;
  }
}
